import { useState, useEffect, useCallback } from "react";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import MainLayout from "../Themes/MainLayout";
import { getConfirmDeleteDialog } from "../Components/confirmDeleteDialog";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  CssBaseline,
  Chip,
  MenuItem,
  Alert,
  Pagination,
  Tooltip,
  Grid,
  IconButton,
  Popover,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Tabs,
  Tab,
  Drawer,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { useCurrentUser } from "../utils/useCurrentUser";
import { normalizeRole } from "../utils/normalizeRole";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fmt = (d) => {
  if (!d) return "—";
  // Parse date string as local time (avoid UTC→IST timezone shift)
  const date =
    typeof d === "string" && d.length === 10
      ? new Date(d + "T00:00:00")
      : new Date(d);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
const fmtCurrency = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const currentMonthRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const last = new Date(y, now.getMonth() + 1, 0).getDate();
  return {
    from: `${y}-${m}-01`,
    to: `${y}-${m}-${String(last).padStart(2, "0")}`,
  };
};

const monthLabel = (s) => {
  if (!s) return "";
  const [y, mo] = s.split("-");
  return new Date(y, parseInt(mo) - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

// const statusChip = (status) => {
//   const s = (status || "").toLowerCase().trim();
//   if (s === "approved")
//     return <Chip label="Approved" size="small" sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }} />;
//   if (s === "rejected")
//     return <Chip label="Rejected" size="small" sx={{ bgcolor: "#ffebee", color: "#c62828", fontWeight: 600 }} />;
//   if (s.startsWith("pending, "))
//     return <Chip label={status} size="small" sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />;
//   if (s === "waiting for admin approval")
//     return <Chip label="Awaiting Admin" size="small" sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }} />;
//   if (s === "waiting for system admin approval")
//     return <Chip label="Awaiting System Admin" size="small" sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }} />;
//   // Default: Pending = Awaiting TL
//   return <Chip label="Awaiting TL" size="small" sx={{ bgcolor: "#fff8e1", color: "#e65100", fontWeight: 600 }} />;
// };
const statusChip = (status) => {
  const s = (status || "").toLowerCase().trim();

  if (s === "approved")
    return (
      <Chip
        label="Approved"
        size="small"
        sx={{ bgcolor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
      />
    );

  if (s === "rejected")
    return (
      <Chip
        label="Rejected"
        size="small"
        sx={{ bgcolor: "#ffebee", color: "#c62828", fontWeight: 600 }}
      />
    );

  // "Partial: 1/N" → TL approved, waiting for PM
  // "Partial: 2/N" → PM approved, waiting for HR
  // "Partial: 3/N" → HR approved, waiting for Admin
  const partialMatch = s.match(/^partial:\s*(\d+)\/(\d+)$/);
  if (partialMatch) {
    const done = parseInt(partialMatch[1]);
    const nextRole = done === 1 ? "Manager" : done === 2 ? "HR" : "Admin";
    return (
      <Chip
        label={`Awaiting ${nextRole}`}
        size="small"
        sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
      />
    );
  }

  if (s === "awaiting admin")
    return (
      <Chip
        label="Awaiting Admin"
        size="small"
        sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }}
      />
    );

  if (s === "awaiting sysadmin")
    return (
      <Chip
        label="Awaiting Sys Admin"
        size="small"
        sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a", fontWeight: 600 }}
      />
    );

  // Default: "Pending" = submitted, waiting for TL to pick up
  return (
    <Chip
      label="Awaiting TL"
      size="small"
      sx={{ bgcolor: "#fff8e1", color: "#e65100", fontWeight: 600 }}
    />
  );
};

const KPICard = ({ icon, label, value, color, bg, border }) => (
  <Box
    sx={{
      border: `1px solid ${border}`,
      borderRadius: 2,
      px: 2.5,
      py: 1.5,
      flex: 1,
      minWidth: 150,
      backgroundColor: bg,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    <Box sx={{ color, display: "flex" }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={700}
        color={color}
        sx={{ fontSize: 18 }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const ALL_COLS = [
  { key: "sno", label: "#", filterable: false },
  { key: "allowance_id", label: "Allowance Type", filterable: true },
  { key: "allowance_desc", label: "Description", filterable: true },
  { key: "allowance_date", label: "Allowance Date", filterable: true },
  { key: "period", label: "Period", filterable: false },
  { key: "no_of_days", label: "Days", filterable: true },
  // { key: "allowance_amount", label: "Amount", filterable: true },
  { key: "notes", label: "Notes", filterable: true },
  { key: "approved_by", label: "Approved By", filterable: true },
  { key: "approval_status", label: "Status", filterable: true },
];

const defaultForm = () => ({
  allowance_id: "",
  allowance_date: new Date().toISOString().slice(0, 10),
  start_date: "",
  end_date: "",
  no_of_days: "",
  notes: "",
});

export default function EmployeeAllowance() {
  const { empId, boId, roles } = useCurrentUser();
  const init = currentMonthRange();

  // Normalize roles same as LeaveApproval
  const userRole = (() => {
    const roleList = Array.isArray(roles) ? roles : [roles || ""];
    for (const r of roleList) {
      const name = (
        typeof r === "object" ? r.role_name || r.name || "" : String(r)
      )
        .toLowerCase()
        .trim();
      if (name.includes("system admin")) return "System Admin";
      if (name === "admin" || name.includes("administrator")) return "Admin";
      if (name.includes("human resource") || ["hr", "hrs"].includes(name))
        return "HR";
      if (name.includes("project manager") || ["pm", "pms"].includes(name))
        return "Project Manager";
      if (
        name.includes("technical lead") ||
        name.includes("tech lead") ||
        ["tl", "tls"].includes(name)
      )
        return "Technical Lead";
    }
    return "Employee";
  })();

  const isApprover = [
    "Technical Lead",
    "Project Manager",
    "HR",
    "Admin",
    "System Admin",
  ].includes(userRole);
  const isAdmin = ["Admin", "System Admin"].includes(userRole);

  const [activeTab, setActiveTab] = useState(0);
  const [empName, setEmpName] = useState("");
  const [fromDate, setFromDate] = useState(init.from);
  const [toDate, setToDate] = useState(init.to);
  const [filterDateError, setFilterDateError] = useState(""); // My Allowance toolbar
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [globalSearch, setGlobalSearch] = useState("");
  const [visibleCols, setVisibleCols] = useState(
    Object.fromEntries(ALL_COLS.map((c) => [c.key, true])),
  );
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [colFilters, setColFilters] = useState({});
  const [filterAnchors, setFilterAnchors] = useState({});
  const [allowanceTypes, setAllowanceTypes] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete confirm dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Approval tab state
  const [pendingRows, setPendingRows] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalAlert, setApprovalAlert] = useState({ type: "", msg: "" });
  const [drawerRow, setDrawerRow] = useState(null);
  const [drawerRemarks, setDrawerRemarks] = useState("");
  const [actioning, setActioning] = useState(false);
  const [approvalSearch, setApprovalSearch] = useState("");
  const [historyOpen, setHistoryOpen] = useState({});
  const [historyData, setHistoryData] = useState({});

  const [salaryFrom, setSalaryFrom] = useState(init.from);
  const [salaryTo, setSalaryTo] = useState(init.to);
  const [salaryDateError, setSalaryDateError] = useState(""); // Allowance History toolbar
  const [salaryData, setSalaryData] = useState({
    employees: [],
    grand_total: 0,
    total_records: 0,
  });
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [salaryAlert, setSalaryAlert] = useState({ type: "", msg: "" });
  const [salarySearch, setSalarySearch] = useState("");

  // fetch employee name
  useEffect(() => {
    if (!empId || !boId) return;
    fetch(`${BASE_URL}/EmployeeDetails/${boId}/${empId}`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setEmpName(`${d.first_name || ""} ${d.last_name || ""}`.trim());
      })
      .catch(() => {});
  }, [empId, boId]);

  // fetch allowance types
  useEffect(() => {
    if (!boId) return;
    fetch(`${BASE_URL}/api/allowance/types?bo_id=${boId}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => setAllowanceTypes(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [boId]);

  // fetch my allowance records
  const fetchData = useCallback(
    async (from, to) => {
      if (!empId || !boId) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({
          bo_id: boId,
          emp_id: empId,
          start_date: from,
          end_date: to,
        });
        const res = await fetch(
          `${BASE_URL}/api/allowance/employee?${params}`,
          { credentials: "include" },
        );
        const data = await res.json();
        setAllRows(data.records || []);
        setPage(1);
      } catch {
        setAlert({ type: "error", msg: "Failed to fetch allowance data." });
      } finally {
        setLoading(false);
      }
    },
    [empId, boId],
  );

  // Auto-fetch when dates change (same behaviour as Allowance History tab)
  useEffect(() => {
    if (fromDate && toDate && fromDate > toDate) {
      setFilterDateError("From date must be before To date");
      return;
    }
    setFilterDateError("");
    if (empId && boId && fromDate && toDate) fetchData(fromDate, toDate);
  }, [empId, boId, fromDate, toDate]); // eslint-disable-line

  // fetch pending approvals
  const fetchPending = useCallback(async () => {
    if (!boId) return;
    setApprovalLoading(true);
    try {
      // const params = new URLSearchParams({ bo_id: boId, role: userRole });
      const params = new URLSearchParams({
        bo_id: boId,
        role: userRole,
        userId: empId,
      });
      const res = await fetch(
        `${BASE_URL}/api/allowance/pending?${params.toString()}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPendingRows(data.records || []);
    } catch (err) {
      console.error("Pending approvals error:", err.message);
      setApprovalAlert({
        type: "error",
        msg: "Failed to fetch pending approvals.",
      });
    } finally {
      setApprovalLoading(false);
    }
  }, [boId, userRole]);

  useEffect(() => {
    if (isApprover && activeTab === 1) fetchPending();
  }, [isApprover, activeTab, fetchPending]);

  useEffect(() => {
    if (alert.msg) {
      const t = setTimeout(() => setAlert({ type: "", msg: "" }), 4000);
      return () => clearTimeout(t);
    }
  }, [alert.msg]);
  useEffect(() => {
    if (approvalAlert.msg) {
      const t = setTimeout(() => setApprovalAlert({ type: "", msg: "" }), 4000);
      return () => clearTimeout(t);
    }
  }, [approvalAlert.msg]);

  // auto-calc days
  useEffect(() => {
    if (form.start_date && form.end_date) {
      const diff =
        Math.round(
          (new Date(form.end_date) - new Date(form.start_date)) / 86400000,
        ) + 1;
      if (diff > 0) setForm((p) => ({ ...p, no_of_days: diff }));
    }
  }, [form.start_date, form.end_date]);

  const fetchSalaryView = useCallback(
    async (from, to) => {
      if (!boId) return;
      setSalaryLoading(true);
      setSalaryAlert({ type: "", msg: "" });
      try {
        const params = new URLSearchParams({
          bo_id: boId,
          start_date: from,
          end_date: to,
        });
        const res = await fetch(
          `${BASE_URL}/api/allowance/admin/salary-view?${params}`,
          { credentials: "include" },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setSalaryData(data);
      } catch {
        setSalaryAlert({ type: "error", msg: "Failed to fetch salary data." });
      } finally {
        setSalaryLoading(false);
      }
    },
    [boId],
  );

  // Auto-fetch when dates change (same behaviour as My Allowance tab)
  useEffect(() => {
    if (salaryFrom && salaryTo && salaryFrom > salaryTo) {
      setSalaryDateError("From date must be before To date");
      return;
    }
    setSalaryDateError("");
    if (isAdmin && salaryFrom && salaryTo)
      fetchSalaryView(salaryFrom, salaryTo);
  }, [salaryFrom, salaryTo]); // eslint-disable-line

  const selectedTypeData = allowanceTypes.find(
    (a) => a.allowance_id === form.allowance_id,
  );

  // Toggle history inline + fetch once
  const toggleHistory = useCallback(
    async (uniqueid) => {
      setHistoryOpen((p) => ({ ...p, [uniqueid]: !p[uniqueid] }));
      if (!historyData[uniqueid]) {
        try {
          const res = await fetch(
            `${BASE_URL}/api/allowance/${uniqueid}/history/${boId}`,
            { credentials: "include" },
          );
          const data = await res.json();
          setHistoryData((p) => ({
            ...p,
            [uniqueid]: Array.isArray(data) ? data : [],
          }));
        } catch {
          setHistoryData((p) => ({ ...p, [uniqueid]: [] }));
        }
      }
    },
    [historyData, boId],
  );

  // Filtered pending with search
  const filteredPending = pendingRows.filter((row) => {
    if (!approvalSearch.trim()) return true;
    const q = approvalSearch.toLowerCase();
    return [
      row.emp_name,
      row.emp_id,
      row.allowance_id,
      row.allowance_desc,
      String(row.allowance_amount || ""),
      row.approval_status || "",
    ].some((v) => v && String(v).toLowerCase().includes(q));
  });

  // Top claimants from pending rows — with role-aware approved/rejected/pending breakdown
  // For TL: "Partial: 1/x" = TL already approved → count as Approved
  // For PM: "Partial: 2/x" = PM already approved → count as Approved
  // For HR/Admin/SysAdmin: "Approved" = fully approved
  const getEffectiveStatus = (approvalStatus, role) => {
    const s = (approvalStatus || "").toLowerCase().trim();
    if (s === "approved") return "approved";
    if (s === "rejected") return "rejected";
    const partialMatch = s.match(/^partial:\s*(\d+)\/(\d+)$/);
    if (partialMatch) {
      const done = parseInt(partialMatch[1]);
      // TL approved at step 1, PM at step 2, HR at step 3
      if (role === "Technical Lead" && done >= 1) return "approved";
      if (role === "Project Manager" && done >= 2) return "approved";
      if (role === "HR" && done >= 3) return "approved";
      return "pending";
    }
    if (s === "awaiting admin") {
      if (["Admin", "System Admin"].includes(role)) return "pending";
      // For lower roles, request has passed through them → approved from their view
      return ["Technical Lead", "Project Manager", "HR"].includes(role)
        ? "approved"
        : "pending";
    }
    if (s === "awaiting sysadmin") {
      if (role === "System Admin") return "pending";
      return "approved"; // already passed all lower levels
    }
    return "pending"; // "Pending" = waiting for TL = not yet actioned
  };

  const topClaimants = Object.values(
    pendingRows.reduce((acc, r) => {
      const key = r.emp_id;
      if (!acc[key])
        acc[key] = {
          emp_id: key,
          emp_name: r.emp_name || key,
          count: 0,
          total: 0,
          approved_count: 0,
          rejected_count: 0,
          pending_count: 0,
          approved_amount: 0,
          rejected_amount: 0,
        };
      acc[key].count += 1;
      acc[key].total += parseInt(r.allowance_amount) || 0;
      const eff = getEffectiveStatus(r.approval_status, userRole);
      if (eff === "approved") {
        acc[key].approved_count += 1;
        acc[key].approved_amount += parseInt(r.allowance_amount) || 0;
      } else if (eff === "rejected") {
        acc[key].rejected_count += 1;
        acc[key].rejected_amount += parseInt(r.allowance_amount) || 0;
      } else {
        acc[key].pending_count += 1;
      }
      return acc;
    }, {}),
  )
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);

  // filtered rows
  const filteredRows = allRows.filter((row) => {
    if (globalSearch.trim()) {
      const q = globalSearch.toLowerCase();
      if (
        ![
          row.allowance_id,
          row.allowance_desc,
          row.notes,
          row.approved_by_name || row.approved_by,
          String(row.allowance_amount || ""),
          fmt(row.allowance_date),
          row.approval_status || "",
        ].some((v) => v && String(v).toLowerCase().includes(q))
      )
        return false;
    }
    for (const [col, val] of Object.entries(colFilters)) {
      if (!val) continue;
      const q = val.toLowerCase();
      const map = {
        allowance_id: row.allowance_id || "",
        allowance_desc: row.allowance_desc || "",
        allowance_date: fmt(row.allowance_date),
        no_of_days: String(row.no_of_days || ""),
        allowance_amount: String(row.allowance_amount || ""),
        notes: row.notes || "",
        approved_by: row.approved_by_name || row.approved_by || "",
        approval_status: row.approval_status || "",
      };
      if (map[col] !== undefined && !map[col].toLowerCase().includes(q))
        return false;
    }
    return true;
  });

  const totalAmount = filteredRows.reduce(
    (s, r) => s + (parseInt(r.allowance_amount) || 0),
    0,
  );
  const uniqueTypes = [...new Set(filteredRows.map((r) => r.allowance_id))]
    .length;
  const pendingCount = filteredRows.filter(
    (r) => (r.approval_status || "").toLowerCase() === "pending",
  ).length;

  const monthBreakdown = filteredRows.reduce((acc, r) => {
    if (!r.allowance_date) return acc;
    const raw = r.allowance_date;
    // Parse as local time to avoid UTC→IST timezone shift
    const d =
      typeof raw === "string" && raw.length >= 10
        ? new Date(raw.slice(0, 10) + "T00:00:00")
        : new Date(raw);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    acc[key] = (acc[key] || 0) + (parseInt(r.allowance_amount) || 0);
    return acc;
  }, {});

  const typeBreakdown = filteredRows.reduce((acc, r) => {
    const key = r.allowance_id || "—";
    if (!acc[key])
      acc[key] = { desc: r.allowance_desc || key, total: 0, count: 0 };
    acc[key].total += parseInt(r.allowance_amount) || 0;
    acc[key].count += 1;
    return acc;
  }, {});

  const paginated = filteredRows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  const handleOpenAdd = () => {
    setForm(defaultForm());
    setFormErrors({});
    setIsEditing(false);
    setEditId(null);
    setDialogOpen(true);
  };
  const handleOpenEdit = (row) => {
    setForm({
      allowance_id: row.allowance_id || "",
      allowance_date: row.allowance_date ? row.allowance_date.slice(0, 10) : "",
      start_date: row.start_date ? row.start_date.slice(0, 10) : "",
      end_date: row.end_date ? row.end_date.slice(0, 10) : "",
      no_of_days: row.no_of_days || "",
      notes: row.notes || "",
    });
    setFormErrors({});
    setIsEditing(true);
    setEditId(row.uniqueid);
    setDialogOpen(true);
  };

  const validateForm = () => {
    const e = {};
    if (!form.allowance_id) e.allowance_id = "Allowance type is required";
    if (!form.allowance_date?.trim())
      e.allowance_date = "Allowance date is required";
    if (!form.start_date?.trim()) e.start_date = "Start date is required";
    if (!form.end_date?.trim()) e.end_date = "End date is required";
    if (!form.no_of_days || parseInt(form.no_of_days) < 1)
      e.no_of_days = "Number of days is required";
    if (
      form.start_date &&
      form.end_date &&
      new Date(form.end_date) < new Date(form.start_date)
    )
      e.end_date = "End date must be after start date";
    return e;
  };

  const handleSave = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, emp_id: empId, bo_id: boId };
      const url = isEditing
        ? `${BASE_URL}/api/allowance/${editId}`
        : `${BASE_URL}/api/allowance`;
      const method = isEditing ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();

      // Fire-and-forget: notify first approver by mail (same as leave request flow)
      // if (!isEditing && data.uniqueid) {
      //   fetch(`${BASE_URL}/api/allowance/${data.uniqueid}/notify-submit`, {
      //     method: "POST",
      //     headers: { "Content-Type": "application/json" },
      //     credentials: "include",
      //     body: JSON.stringify({ bo_id: boId, emp_id: empId }),
      //   }).catch(() => {});
      // }

      setAlert({
        type: "success",
        msg: isEditing
          ? "Allowance updated!"
          : "Allowance submitted for approval!",
      });
      setDialogOpen(false);
      fetchData(fromDate, toDate);
    } catch {
      setAlert({ type: "error", msg: "Failed to save. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (uniqueid) => {
    setDeleteTargetId(uniqueid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    try {
      await fetch(
        `${BASE_URL}/api/allowance/${deleteTargetId}?bo_id=${boId}&emp_id=${empId}`,
        { method: "DELETE", credentials: "include" },
      );
      setAlert({ type: "success", msg: "Deleted successfully." });
      fetchData(fromDate, toDate);
    } catch {
      setAlert({ type: "error", msg: "Delete failed." });
    } finally {
      setDeleteTargetId(null);
    }
  };

  const handleApprove = async () => {
    if (!drawerRow) return;
    setActioning(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/allowance/${drawerRow.uniqueid}/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            bo_id: boId,
            approved_by: empId,
            userRole,
            remarks: drawerRemarks,
          }),
        },
      );
      if (!res.ok) throw new Error("Approve failed");
      setApprovalAlert({ type: "success", msg: "Allowance approved!" });
      setDrawerRow(null);
      setDrawerRemarks("");
      fetchPending();
    } catch {
      setApprovalAlert({ type: "error", msg: "Approval failed." });
    } finally {
      setActioning(false);
    }
  };

  const handleReject = async () => {
    if (!drawerRow) return;
    setActioning(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/allowance/${drawerRow.uniqueid}/reject`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            bo_id: boId,
            rejected_by: empId,
            remarks: drawerRemarks,
          }),
        },
      );
      if (!res.ok) throw new Error("Reject failed");
      setApprovalAlert({ type: "success", msg: "Allowance rejected." });
      setDrawerRow(null);
      setDrawerRemarks("");
      fetchPending();
    } catch {
      setApprovalAlert({ type: "error", msg: "Rejection failed." });
    } finally {
      setActioning(false);
    }
  };
  // ── Salary row with expand/collapse ──────────────────────────────────────────
  function SalaryEmpRow({ emp, idx }) {
    const [open, setOpen] = useState(false);
    return (
      <>
        <TableRow
          onClick={() => setOpen((p) => !p)}
          sx={{
            backgroundColor: idx % 2 === 0 ? "#fafcff" : "#fff",
            cursor: "pointer",
            "&:hover": { backgroundColor: "#e3f2fd" },
          }}
        >
          <TableCell sx={{ width: 40, py: 0.5 }}>
            <IconButton size="small">
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          </TableCell>
          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>
            {emp.emp_id}
          </TableCell>
          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>
            {emp.emp_name}
          </TableCell>
          <TableCell align="center" sx={{ fontSize: 12 }}>
            {emp.records.length}
          </TableCell>
          <TableCell align="center" sx={{ fontSize: 12 }}>
            {emp.total_days}
          </TableCell>
          <TableCell
            align="right"
            sx={{ fontWeight: 700, color: "#2e7d32", fontSize: 13 }}
          >
            {new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
              maximumFractionDigits: 0,
            }).format(emp.total_amount || 0)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={6} sx={{ p: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  px: 4,
                  py: 1.5,
                  bgcolor: "#f8faff",
                  borderTop: "1px solid #e3f2fd",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="#1a3a5c"
                  display="block"
                  mb={1}
                >
                  Allowance Details — {emp.emp_name}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#e8f0fe" }}>
                      {[
                        "Type",
                        "Description",
                        "Allowance Date",
                        "Period",
                        "Days",
                        // "Amount",
                        "Approved By",
                        "Notes",
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            py: 0.5,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emp.records.map((r, i) => (
                      <TableRow
                        key={r.uniqueid}
                        sx={{ bgcolor: i % 2 === 0 ? "#fff" : "#f5f8ff" }}
                      >
                        <TableCell>
                          <Chip
                            label={r.allowance_id}
                            size="small"
                            sx={{
                              bgcolor: "#e3f2fd",
                              color: "#1565c0",
                              fontWeight: 700,
                              fontSize: 10,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          {r.allowance_desc || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                          {fmt(r.allowance_date)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
                          {fmt(r.start_date)}
                          {" – "}
                          {fmt(r.end_date)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: 11 }}>
                          {r.no_of_days}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: "#2e7d32",
                            fontSize: 11,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {new Intl.NumberFormat("en-IN", {
                            style: "currency",
                            currency: "INR",
                            maximumFractionDigits: 0,
                          }).format(r.allowance_amount || 0)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          {r.approved_by_name || r.approved_by || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, maxWidth: 140 }}>
                          <Tooltip title={r.notes || "—"}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ fontSize: 11, maxWidth: 130 }}
                            >
                              {r.notes || "—"}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: "#e8f5e9" }}>
                      <TableCell
                        colSpan={4}
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      >
                        Sub-Total
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      >
                        {emp.total_days}
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#2e7d32", fontSize: 12 }}
                      >
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                          maximumFractionDigits: 0,
                        }).format(emp.total_amount || 0)}
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }

  return (
    <MainLayout>
      <ThemeProvider theme={Table_theme}>
        <CssBaseline />

        {/* Header */}
        {/* <Box
          sx={{
            background: "linear-gradient(135deg, #1a3a5c 0%, #1976d2 100%)",
            borderRadius: 2,
            px: 3,
            py: 2,
            mb: 3,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="#fff">
              Employee Allowance
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.75)">
              Claim and track your allowances
            </Typography>
          </Box>
        </Box> */}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab label="My Allowance" />
            {isApprover && (
              <Tab
                label={`Pending Approvals${pendingRows.length ? ` (${pendingRows.length})` : ""}`}
              />
            )}
            {isAdmin && <Tab label="Allowance History" />}
          </Tabs>
        </Box>

        {/* ═══════════════════════════════════
            TAB 0 — My Allowance
        ═══════════════════════════════════ */}
        {activeTab === 0 && (
          <>
            {alert.msg && (
              <Alert
                severity={alert.type}
                sx={{ mb: 2 }}
                onClose={() => setAlert({ type: "", msg: "" })}
              >
                {alert.msg}
              </Alert>
            )}

            {/* Toolbar */}
            <Box
              display="flex"
              flexWrap="wrap"
              gap={1.5}
              alignItems="flex-start"
              mb={filterDateError ? 0.5 : 2}
            >
              <Tooltip title="Column Visibility">
                <IconButton
                  size="small"
                  sx={{ mt: 0.5 }}
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                >
                  <SettingsIcon style={{ width: 22, height: 22 }} />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                  height: 36,
                  borderRadius: 2,
                  backgroundColor: "#2DB0E4",
                  fontWeight: 700,
                }}
              >
                Add
              </Button>
              <TextField
                size="small"
                placeholder="Search..."
                value={globalSearch}
                onChange={(e) => {
                  setGlobalSearch(e.target.value);
                  setPage(1);
                }}
                sx={{ width: 220, "& input": { fontSize: 13 } }}
                InputProps={{
                  sx: { height: 36 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160, "& input": { fontSize: 13 } }}
              />
              <TextField
                label="To Date"
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                error={!!filterDateError}
                helperText={filterDateError}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160, "& input": { fontSize: 13 } }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const r = currentMonthRange();
                  setFromDate(r.from);
                  setToDate(r.to);
                }}
                sx={{ height: 36, borderRadius: 2, fontSize: 12 }}
              >
                Reset
              </Button>
            </Box>

            {/* Column visibility popover */}
            <Popover
              open={Boolean(settingsAnchor)}
              anchorEl={settingsAnchor}
              onClose={() => setSettingsAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { p: 1, width: 230, borderRadius: 2 } }}
            >
              {ALL_COLS.filter((c) => c.key !== "sno").map((col) => (
                <Box
                  key={col.key}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    border: "1px solid #ccc",
                    borderRadius: 1.5,
                    px: 2,
                    py: 0.8,
                    mb: 0.8,
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: "bold" }}>
                    {col.label}
                  </Typography>
                  <Switch
                    size="small"
                    checked={visibleCols[col.key]}
                    onChange={(e) =>
                      setVisibleCols((p) => ({
                        ...p,
                        [col.key]: e.target.checked,
                      }))
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2DB0E4",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        { backgroundColor: "#2DB0E4" },
                    }}
                  />
                </Box>
              ))}
            </Popover>

            {/* KPI Cards */}
            <Box display="flex" flexWrap="wrap" gap={2} mb={2.5}>
              {/* <KPICard
                icon={<MonetizationOnOutlinedIcon />}
                label="Total Amount"
                value={fmtCurrency(totalAmount)}
                color="#1565c0"
                bg="#f0f7ff"
                border="#d0e4f7"
              /> */}
              <KPICard
                icon={<ReceiptLongOutlinedIcon />}
                label="Total Records"
                value={filteredRows.length}
                color="#2e7d32"
                bg="#f1f8e9"
                border="#c8e6c9"
              />
              <KPICard
                icon={<CategoryOutlinedIcon />}
                label="Pending"
                value={pendingCount}
                color="#e65100"
                bg="#fff3e0"
                border="#ffe0b2"
              />
              <KPICard
                icon={<CalendarMonthOutlinedIcon />}
                label="Period"
                value={`${fmt(fromDate)} to ${fmt(toDate)}`}
                color="#6a1b9a"
                bg="#f3e5f5"
                border="#e1bee7"
              />
            </Box>

            {/* Main Table */}
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #e0e8f0",
                borderRadius: 2,
                overflow: "hidden",
                mb: 3,
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          textAlign: "center",
                        }}
                      >
                        Actions
                      </TableCell>
                      {ALL_COLS.filter((c) => visibleCols[c.key]).map((col) => (
                        <TableCell
                          key={col.key}
                          sx={{
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                            fontSize: 12,
                          }}
                        >
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                          >
                            {col.label}
                            {col.filterable && (
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  setFilterAnchors((p) => ({
                                    ...p,
                                    [col.key]: e.currentTarget,
                                  }))
                                }
                                sx={{ p: 0.3 }}
                              >
                                <FilterListIcon
                                  sx={{
                                    fontSize: 16,
                                    color: colFilters[col.key]
                                      ? "#1976d2"
                                      : "inherit",
                                  }}
                                />
                              </IconButton>
                            )}
                          </Box>
                          {col.filterable && (
                            <Popover
                              open={Boolean(filterAnchors[col.key])}
                              anchorEl={filterAnchors[col.key]}
                              onClose={() =>
                                setFilterAnchors((p) => ({
                                  ...p,
                                  [col.key]: null,
                                }))
                              }
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <Box
                                p={1.5}
                                display="flex"
                                flexDirection="column"
                                gap={1}
                                width={200}
                              >
                                <TextField
                                  size="small"
                                  placeholder={`Filter ${col.label}`}
                                  value={colFilters[col.key] || ""}
                                  onChange={(e) => {
                                    setColFilters((p) => ({
                                      ...p,
                                      [col.key]: e.target.value,
                                    }));
                                    setPage(1);
                                  }}
                                  autoFocus
                                />
                                <Box
                                  display="flex"
                                  justifyContent="flex-end"
                                  gap={1}
                                >
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() =>
                                      setFilterAnchors((p) => ({
                                        ...p,
                                        [col.key]: null,
                                      }))
                                    }
                                  >
                                    Apply
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setColFilters((p) => ({
                                        ...p,
                                        [col.key]: "",
                                      }));
                                      setFilterAnchors((p) => ({
                                        ...p,
                                        [col.key]: null,
                                      }));
                                    }}
                                  >
                                    Clear
                                  </Button>
                                </Box>
                              </Box>
                            </Popover>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={ALL_COLS.length + 1}
                          align="center"
                          sx={{ py: 4 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Loading...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : paginated.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={ALL_COLS.length + 1}
                          align="center"
                          sx={{ py: 5 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No allowance records found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginated.map((row, idx) => {
                        return (
                          <TableRow
                            key={row.uniqueid}
                            sx={{
                              backgroundColor:
                                idx % 2 === 0 ? "#fafcff" : "#fff",
                              "&:hover": { backgroundColor: "#e3f2fd" },
                            }}
                          >
                            <TableCell
                              sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                            >
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEdit(row)}
                                  sx={{ color: "#2DB0E4" }}
                                >
                                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleDeleteClick(row.uniqueid)
                                  }
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                            {visibleCols.sno && (
                              <TableCell sx={{ fontSize: 12 }}>
                                {(page - 1) * rowsPerPage + idx + 1}
                              </TableCell>
                            )}
                            {visibleCols.allowance_id && (
                              <TableCell>
                                <Chip
                                  label={row.allowance_id}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#e3f2fd",
                                    color: "#1565c0",
                                    fontWeight: 700,
                                    fontSize: 11,
                                  }}
                                />
                              </TableCell>
                            )}
                            {visibleCols.allowance_desc && (
                              <TableCell sx={{ fontSize: 12, maxWidth: 180 }}>
                                <Tooltip title={row.allowance_desc || "—"}>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{ maxWidth: 170, fontSize: 12 }}
                                  >
                                    {row.allowance_desc || "—"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            )}
                            {visibleCols.allowance_date && (
                              <TableCell
                                sx={{ fontSize: 12, whiteSpace: "nowrap" }}
                              >
                                {fmt(row.allowance_date)}
                              </TableCell>
                            )}
                            {visibleCols.period && (
                              <TableCell
                                sx={{ fontSize: 12, whiteSpace: "nowrap" }}
                              >
                                {fmt(row.start_date)} – {fmt(row.end_date)}
                              </TableCell>
                            )}
                            {visibleCols.no_of_days && (
                              <TableCell align="center" sx={{ fontSize: 12 }}>
                                {row.no_of_days}
                              </TableCell>
                            )}
                            {visibleCols.allowance_amount && (
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "#2e7d32",
                                  fontSize: 12,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {fmtCurrency(row.allowance_amount)}
                              </TableCell>
                            )}
                            {visibleCols.notes && (
                              <TableCell sx={{ fontSize: 12, maxWidth: 150 }}>
                                <Tooltip title={row.notes || "—"}>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{ maxWidth: 140, fontSize: 12 }}
                                  >
                                    {row.notes || "—"}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                            )}
                            {visibleCols.approved_by && (
                              <TableCell sx={{ fontSize: 12 }}>
                                {/* Show approver name only when fully Approved or Rejected */}
                                {["approved", "rejected"].includes(
                                  (row.approval_status || "")
                                    .toLowerCase()
                                    .trim(),
                                )
                                  ? row.approved_by_name ||
                                    row.approved_by ||
                                    "—"
                                  : "—"}
                              </TableCell>
                            )}
                            {visibleCols.approval_status && (
                              <TableCell>
                                {statusChip(row.approval_status)}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                    {filteredRows.length > 0 && (
                      <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                        <TableCell
                          colSpan={
                            ALL_COLS.filter(
                              (c) =>
                                visibleCols[c.key] &&
                                c.key !== "allowance_amount",
                            ).length + 1
                          }
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: "#1a3a5c",
                          }}
                        >
                          Total
                        </TableCell>
                        {visibleCols.allowance_amount && (
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              color: "#2e7d32",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fmtCurrency(totalAmount)}
                          </TableCell>
                        )}
                        {["notes", "approved_by", "approval_status"]
                          .filter((k) => visibleCols[k])
                          .map((k) => (
                            <TableCell key={k} />
                          ))}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" py={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                    sx={{
                      "& .Mui-selected": {
                        backgroundColor: "#b3dbea !important",
                        fontWeight: "bold",
                      },
                    }}
                  />
                </Box>
              )}
            </Paper>

            {/* Summary Breakdown */}
            {filteredRows.length > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: "1px solid #e0e8f0",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ backgroundColor: "#1a3a5c", px: 2, py: 1.2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="#fff"
                      >
                        Monthly Breakdown
                      </Typography>
                    </Box>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                            Month
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, fontSize: 12 }}
                          >
                            Total Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(monthBreakdown)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([month, amt], i) => (
                            <TableRow
                              key={month}
                              sx={{
                                backgroundColor:
                                  i % 2 === 0 ? "#fafcff" : "#fff",
                              }}
                            >
                              <TableCell sx={{ fontSize: 12 }}>
                                {monthLabel(month + "-01")}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 700,
                                  color: "#2e7d32",
                                  fontSize: 12,
                                }}
                              >
                                {fmtCurrency(amt)}
                              </TableCell>
                            </TableRow>
                          ))}
                        <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                            Grand Total
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: "#2e7d32",
                              fontSize: 13,
                            }}
                          >
                            {fmtCurrency(totalAmount)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: "1px solid #e0e8f0",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ backgroundColor: "#1a3a5c", px: 2, py: 1.2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="#fff"
                      >
                        Allowance Type Breakdown
                      </Typography>
                    </Box>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                            Type
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>
                            Description
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, fontSize: 12 }}
                          >
                            Records
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, fontSize: 12 }}
                          >
                            Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(typeBreakdown)
                          .sort(([, a], [, b]) => b.total - a.total)
                          .map(([type, info], i) => (
                            <TableRow
                              key={type}
                              sx={{
                                backgroundColor:
                                  i % 2 === 0 ? "#fafcff" : "#fff",
                              }}
                            >
                              <TableCell>
                                <Chip
                                  label={type}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#e3f2fd",
                                    color: "#1565c0",
                                    fontWeight: 700,
                                    fontSize: 11,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: 12 }}>
                                {info.desc}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 12 }}>
                                {info.count}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 700,
                                  color: "#2e7d32",
                                  fontSize: 12,
                                }}
                              >
                                {fmtCurrency(info.total)}
                              </TableCell>
                            </TableRow>
                          ))}
                        <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                          <TableCell
                            colSpan={2}
                            sx={{ fontWeight: 700, fontSize: 12 }}
                          >
                            Grand Total
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, fontSize: 12 }}
                          >
                            {filteredRows.length}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: "#2e7d32",
                              fontSize: 13,
                            }}
                          >
                            {fmtCurrency(totalAmount)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </>
        )}
        {isApprover && activeTab === 1 && (
          <>
            {approvalAlert.msg && (
              <Alert
                severity={approvalAlert.type}
                sx={{ mb: 2 }}
                onClose={() => setApprovalAlert({ type: "", msg: "" })}
              >
                {approvalAlert.msg}
              </Alert>
            )}

            {/* Search + Refresh toolbar */}
            <Box display="flex" gap={1.5} alignItems="center" mb={2}>
              <TextField
                size="small"
                placeholder="Search employee, type, amount..."
                value={approvalSearch}
                onChange={(e) => setApprovalSearch(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={fetchPending}
                sx={{ borderRadius: 2 }}
              >
                Reset
              </Button>
              <Box flex={1} />
              <Typography variant="caption" color="text.secondary">
                {filteredPending.length} record
                {filteredPending.length !== 1 ? "s" : ""}
              </Typography>
            </Box>

            {approvalLoading ? (
              <Typography color="text.secondary">Loading...</Typography>
            ) : filteredPending.length === 0 ? (
              <Typography color="text.secondary">
                No allowances pending approval.
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {/* Main approvals table — full width */}
                <Grid item xs={12}>
                  <TableContainer
                    component={Paper}
                    sx={{
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      width: "100%",
                      overflowX: "auto",
                    }}
                    elevation={0}
                  >
                    <Table
                      size="small"
                      sx={{ tableLayout: "fixed", minWidth: 750 }}
                    >
                      <TableHead>
                        <TableRow>
                          {[
                            { label: "Employee", width: "14%" },
                            { label: "Type", width: "9%" },
                            { label: "Description", width: "14%" },
                            { label: "Date", width: "10%" },
                            { label: "Period", width: "16%" },
                            { label: "Days", width: "6%" },
                            { label: "Amount", width: "10%" },
                            { label: "Status", width: "12%" },
                            { label: "Actions", width: "9%" },
                          ].map((h) => (
                            <TableCell
                              key={h.label}
                              sx={{
                                fontWeight: "bold",
                                fontSize: 12,
                                whiteSpace: "nowrap",
                                width: h.width,
                              }}
                            >
                              {h.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredPending.map((row, idx) => (
                          <>
                            <TableRow
                              key={row.uniqueid}
                              sx={{
                                backgroundColor:
                                  idx % 2 === 0 ? "#fafcff" : "#fff",
                                borderLeft: `3px solid ${row.can_act ? "#1976d2" : "#bdbdbd"}`,
                                "&:hover": { backgroundColor: "#e2e2ee" },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>
                                {row.emp_name || row.emp_id}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={row.allowance_id}
                                  size="small"
                                  sx={{
                                    bgcolor: "#e3f2fd",
                                    color: "#1565c0",
                                    fontWeight: 700,
                                    fontSize: 11,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: 12 }}>
                                {row.allowance_desc || "—"}
                              </TableCell>
                              <TableCell
                                sx={{ fontSize: 12, whiteSpace: "nowrap" }}
                              >
                                {fmt(row.allowance_date)}
                              </TableCell>
                              <TableCell
                                sx={{ fontSize: 12, whiteSpace: "nowrap" }}
                              >
                                {fmt(row.start_date)} – {fmt(row.end_date)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 12 }}>
                                {row.no_of_days}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "#2e7d32",
                                  fontSize: 12,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {fmtCurrency(row.allowance_amount)}
                              </TableCell>
                              <TableCell>
                                {statusChip(row.approval_status)}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {row.can_act && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                      setDrawerRow(row);
                                      setDrawerRemarks("");
                                    }}
                                    sx={{
                                      fontSize: 11,
                                      px: 1.5,
                                      borderRadius: 2,
                                      mr: 0.5,
                                    }}
                                  >
                                    Review
                                  </Button>
                                )}
                                <Tooltip
                                  title={
                                    historyOpen[row.uniqueid]
                                      ? "Hide History"
                                      : "Show History"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleHistory(row.uniqueid)}
                                    sx={{ color: "#1976d2" }}
                                  >
                                    <ReceiptLongOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>

                            {/* Inline history row */}
                            {historyOpen[row.uniqueid] && (
                              <TableRow key={`hist-${row.uniqueid}`}>
                                <TableCell
                                  colSpan={9}
                                  sx={{ p: 0, bgcolor: "#f8f9fa" }}
                                >
                                  <Box sx={{ px: 3, py: 1.5 }}>
                                    <Typography
                                      variant="caption"
                                      fontWeight={700}
                                      color="#1a3a5c"
                                      display="block"
                                      mb={1}
                                    >
                                      Approval History
                                    </Typography>
                                    {!historyData[row.uniqueid] ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Loading...
                                      </Typography>
                                    ) : historyData[row.uniqueid].length ===
                                      0 ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        No approval actions yet.
                                      </Typography>
                                    ) : (
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ bgcolor: "#e8f0fe" }}>
                                            {[
                                              "Step",
                                              "Level",
                                              "Approver",
                                              "Status",
                                              "Date",
                                              "Remarks",
                                            ].map((h) => (
                                              <TableCell
                                                key={h}
                                                sx={{
                                                  fontSize: 11,
                                                  fontWeight: 700,
                                                  py: 0.5,
                                                }}
                                              >
                                                {h}
                                              </TableCell>
                                            ))}
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {historyData[row.uniqueid].map(
                                            (h) => (
                                              <TableRow key={h.id}>
                                                <TableCell
                                                  sx={{ fontSize: 11 }}
                                                >
                                                  {h.approval_step}
                                                </TableCell>
                                                <TableCell
                                                  sx={{ fontSize: 11 }}
                                                >
                                                  {h.approval_level}
                                                </TableCell>
                                                <TableCell
                                                  sx={{ fontSize: 11 }}
                                                >
                                                  {h.approver_name ||
                                                    h.approver_id}
                                                </TableCell>
                                                <TableCell>
                                                  {statusChip(
                                                    h.approval_status,
                                                  )}
                                                </TableCell>
                                                <TableCell
                                                  sx={{
                                                    fontSize: 11,
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {fmt(h.approval_date)}
                                                </TableCell>
                                                <TableCell
                                                  sx={{ fontSize: 11 }}
                                                >
                                                  {h.remarks || "—"}
                                                </TableCell>
                                              </TableRow>
                                            ),
                                          )}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* Top Claimants — full width with Approved/Rejected breakdown */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      border: "1px solid #e0e8f0",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ backgroundColor: "#1a3a5c", px: 2, py: 1.2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="#fff"
                      >
                        Approval Summary
                      </Typography>
                    </Box>
                    <Table
                      size="small"
                      sx={{ tableLayout: "fixed", width: "100%" }}
                    >
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f0f7ff" }}>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: 12, width: "22%" }}
                          >
                            Employee
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: 700,
                              fontSize: 12,
                              width: "10%",
                              color: "#1565c0",
                            }}
                          >
                            Total Claims
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: 700,
                              fontSize: 12,
                              width: "10%",
                              color: "#2e7d32",
                            }}
                          >
                            Approved
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: 700,
                              fontSize: 12,
                              width: "10%",
                              color: "#c62828",
                            }}
                          >
                            Rejected
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: 700,
                              fontSize: 12,
                              width: "10%",
                              color: "#e65100",
                            }}
                          >
                            Pending
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              fontSize: 12,
                              width: "13%",
                              color: "#2e7d32",
                            }}
                          >
                            Approved Amt
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              fontSize: 12,
                              width: "13%",
                              color: "#c62828",
                            }}
                          >
                            Rejected Amt
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, fontSize: 12, width: "12%" }}
                          >
                            Total Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topClaimants.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              align="center"
                              sx={{
                                fontSize: 12,
                                color: "text.secondary",
                                py: 3,
                              }}
                            >
                              No data
                            </TableCell>
                          </TableRow>
                        ) : (
                          topClaimants.map((c, i) => (
                            <TableRow
                              key={c.emp_id}
                              sx={{
                                bgcolor: i % 2 === 0 ? "#fafcff" : "#fff",
                                "&:hover": { bgcolor: "#e3f2fd" },
                              }}
                            >
                              <TableCell sx={{ fontSize: 12 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: "50%",
                                      bgcolor:
                                        i === 0
                                          ? "#ffd700"
                                          : i === 1
                                            ? "#c0c0c0"
                                            : i === 2
                                              ? "#cd7f32"
                                              : "#e3f2fd",
                                      color: i < 3 ? "#000" : "#1565c0",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 10,
                                      fontWeight: 700,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {i + 1}
                                  </Box>
                                  <Typography
                                    sx={{ fontSize: 12, fontWeight: 500 }}
                                  >
                                    {c.emp_name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ fontSize: 12, fontWeight: 700 }}
                              >
                                {c.count}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={c.approved_count || 0}
                                  size="small"
                                  sx={{
                                    bgcolor: "#e8f5e9",
                                    color: "#2e7d32",
                                    fontWeight: 700,
                                    fontSize: 11,
                                    minWidth: 36,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={c.rejected_count || 0}
                                  size="small"
                                  sx={{
                                    bgcolor: "#ffebee",
                                    color: "#c62828",
                                    fontWeight: 700,
                                    fontSize: 11,
                                    minWidth: 36,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={c.pending_count || 0}
                                  size="small"
                                  sx={{
                                    bgcolor: "#fff8e1",
                                    color: "#e65100",
                                    fontWeight: 700,
                                    fontSize: 11,
                                    minWidth: 36,
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 700,
                                  color: "#2e7d32",
                                  fontSize: 12,
                                }}
                              >
                                {fmtCurrency(c.approved_amount || 0)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 700,
                                  color: "#c62828",
                                  fontSize: 12,
                                }}
                              >
                                {fmtCurrency(c.rejected_amount || 0)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  fontWeight: 700,
                                  color: "#1565c0",
                                  fontSize: 12,
                                }}
                              >
                                {fmtCurrency(c.total)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* Detail Drawer */}
            <Drawer
              anchor="right"
              open={!!drawerRow}
              onClose={() => setDrawerRow(null)}
              PaperProps={{
                sx: {
                  width: { xs: "100%", sm: 420 },
                  top: "64px",
                  height: "calc(100% - 64px)",
                },
              }}
            >
              {drawerRow && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexShrink: 0,
                      px: 3,
                      pt: 3,
                      pb: 2,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                        {drawerRow.emp_name || drawerRow.emp_id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Allowance Request
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {statusChip(drawerRow.approval_status)}
                      <IconButton
                        size="small"
                        onClick={() => setDrawerRow(null)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Divider sx={{ flexShrink: 0 }} />
                  <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}
                    >
                      {[
                        {
                          label: "Allowance Type",
                          value: `${drawerRow.allowance_id} – ${drawerRow.allowance_desc || ""}`,
                        },
                        {
                          label: "Amount",
                          value: fmtCurrency(drawerRow.allowance_amount),
                        },
                        {
                          label: "Allowance Date",
                          value: fmt(drawerRow.allowance_date),
                        },
                        {
                          label: "Start Date",
                          value: fmt(drawerRow.start_date),
                        },
                        { label: "End Date", value: fmt(drawerRow.end_date) },
                        {
                          label: "No. of Days",
                          value: drawerRow.no_of_days ?? "—",
                        },
                      ].map(({ label, value }) => (
                        <Box key={label} sx={{ minWidth: "45%" }}>
                          <Typography variant="caption" color="text.secondary">
                            {label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: 500,
                              color: label === "Amount" ? "#2e7d32" : "inherit",
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {drawerRow.notes && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Notes
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            mb: 2,
                            borderRadius: 1.5,
                            bgcolor: "#fafafa",
                          }}
                        >
                          <Typography sx={{ fontSize: "0.83rem" }}>
                            {drawerRow.notes}
                          </Typography>
                        </Paper>
                      </>
                    )}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 0.5 }}
                      >
                        Remarks
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={drawerRemarks}
                        onChange={(e) => setDrawerRemarks(e.target.value)}
                        placeholder="Add remarks (optional)..."
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: 3,
                      py: 2,
                      borderTop: "1px solid #e0e0e0",
                    }}
                  >
                    <Box display="flex" gap={1.5}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        disabled={actioning}
                        onClick={handleApprove}
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                      >
                        {actioning ? "Processing..." : "Approve"}
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<CloseIcon />}
                        disabled={actioning}
                        onClick={handleReject}
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                      >
                        {actioning ? "Processing..." : "Reject"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Drawer>
          </>
        )}
        {/* ═══════════════════ TAB 2 — Salary Processing (Admin only) ═══════════════════ */}
        {isAdmin && activeTab === (isApprover ? 2 : 1) && (
          <>
            {salaryAlert.msg && (
              <Alert
                severity={salaryAlert.type}
                sx={{ mb: 2 }}
                onClose={() => setSalaryAlert({ type: "", msg: "" })}
              >
                {salaryAlert.msg}
              </Alert>
            )}

            {/* Toolbar */}
            <Box
              display="flex"
              flexWrap="wrap"
              gap={1.5}
              alignItems="flex-start"
              mb={salaryDateError ? 0.5 : 2}
            >
              <TextField
                size="small"
                placeholder="Search employee, type..."
                value={salarySearch}
                onChange={(e) => setSalarySearch(e.target.value)}
                sx={{ width: 260, mt: 0.3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box flex={1} />
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={salaryFrom}
                onChange={(e) => setSalaryFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160, "& input": { fontSize: 13 } }}
              />
              <TextField
                label="To Date"
                type="date"
                size="small"
                value={salaryTo}
                onChange={(e) => setSalaryTo(e.target.value)}
                error={!!salaryDateError}
                helperText={salaryDateError}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160, "& input": { fontSize: 13 } }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const r = currentMonthRange();
                  setSalaryFrom(r.from);
                  setSalaryTo(r.to);
                }}
                sx={{
                  height: 36,
                  borderRadius: 2,
                  fontSize: 12,
                  alignSelf: "flex-start",
                  mt: 0.3,
                }}
              >
                Reset
              </Button>
            </Box>

            {/* KPI Cards */}
            {(() => {
              const filteredEmps = (salaryData.employees || []).filter(
                (emp) => {
                  if (!salarySearch.trim()) return true;
                  const q = salarySearch.toLowerCase();
                  return [emp.emp_id, emp.emp_name].some(
                    (v) => v && String(v).toLowerCase().includes(q),
                  );
                },
              );
              const grandTotal = filteredEmps.reduce(
                (s, e) => s + e.total_amount,
                0,
              );
              const totalRecs = filteredEmps.reduce(
                (s, e) => s + e.records.length,
                0,
              );

              // allowance type breakdown across filtered employees
              const typeBreakdown = filteredEmps.reduce((acc, emp) => {
                for (const r of emp.records) {
                  const key = r.allowance_id || "—";
                  if (!acc[key])
                    acc[key] = {
                      desc: r.allowance_desc || key,
                      total: 0,
                      count: 0,
                    };
                  acc[key].total += parseInt(r.allowance_amount) || 0;
                  acc[key].count += 1;
                }
                return acc;
              }, {});

              return (
                <>
                  {/* KPI row */}
                  <Box display="flex" flexWrap="wrap" gap={2} mb={2.5}>
                    <KPICard
                      icon={<PeopleAltOutlinedIcon />}
                      label="Employees"
                      value={filteredEmps.length}
                      color="#1565c0"
                      bg="#f0f7ff"
                      border="#d0e4f7"
                    />
                    <KPICard
                      icon={<ReceiptLongOutlinedIcon />}
                      label="Total Records"
                      value={totalRecs}
                      color="#2e7d32"
                      bg="#f1f8e9"
                      border="#c8e6c9"
                    />
                    <KPICard
                      icon={<MonetizationOnOutlinedIcon />}
                      label="Grand Total"
                      value={fmtCurrency(grandTotal)}
                      color="#6a1b9a"
                      bg="#f3e5f5"
                      border="#e1bee7"
                    />
                    <KPICard
                      icon={<CalendarMonthOutlinedIcon />}
                      label="Period"
                      value={`${fmt(salaryFrom)} – ${fmt(salaryTo)}`}
                      color="#e65100"
                      bg="#fff3e0"
                      border="#ffe0b2"
                    />
                  </Box>

                  {salaryLoading ? (
                    <Typography
                      color="text.secondary"
                      sx={{ py: 4, textAlign: "center" }}
                    >
                      Loading...
                    </Typography>
                  ) : filteredEmps.length === 0 ? (
                    <Paper
                      elevation={0}
                      sx={{
                        border: "1px solid #e0e8f0",
                        borderRadius: 2,
                        p: 5,
                        textAlign: "center",
                      }}
                    >
                      <Typography color="text.secondary">
                        No approved allowances found for this period.
                      </Typography>
                    </Paper>
                  ) : (
                    <Grid container spacing={2}>
                      {/* ── Main grouped table ── */}
                      <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={{
                            border: "1px solid #e0e8f0",
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: "#1a3a5c",
                              px: 2,
                              py: 1.2,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              color="#fff"
                            >
                              Approved Allowances — {monthLabel(salaryFrom)}
                              {salaryFrom.slice(0, 7) !== salaryTo.slice(0, 7)
                                ? ` to ${monthLabel(salaryTo)}`
                                : ""}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "rgba(255,255,255,0.7)" }}
                            >
                              Click a row to expand details
                            </Typography>
                          </Box>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: "#f0f7ff" }}>
                                  <TableCell sx={{ width: 40 }} />
                                  <TableCell
                                    sx={{ fontWeight: 700, fontSize: 12 }}
                                  >
                                    Emp ID
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontWeight: 700, fontSize: 12 }}
                                  >
                                    Employee Name
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ fontWeight: 700, fontSize: 12 }}
                                  >
                                    Claims
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ fontWeight: 700, fontSize: 12 }}
                                  >
                                    Total Days
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ fontWeight: 700, fontSize: 12 }}
                                  >
                                    Total Amount
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredEmps.map((emp, idx) => (
                                  <SalaryEmpRow
                                    key={emp.emp_id}
                                    emp={emp}
                                    idx={idx}
                                  />
                                ))}
                                {/* Grand Total */}
                                <TableRow sx={{ bgcolor: "#e8f5e9" }}>
                                  <TableCell
                                    colSpan={3}
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: 12,
                                      color: "#1a3a5c",
                                    }}
                                  >
                                    Grand Total
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ fontWeight: 700, fontSize: 12 }}
                                  >
                                    {totalRecs}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ fontWeight: 700, fontSize: 12 }}
                                  >
                                    {filteredEmps.reduce(
                                      (s, e) => s + e.total_days,
                                      0,
                                    )}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#2e7d32",
                                      fontSize: 13,
                                    }}
                                  >
                                    {fmtCurrency(grandTotal)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Grid>

                      {/* ── Type Summary ── */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            border: "1px solid #e0e8f0",
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{ backgroundColor: "#1a3a5c", px: 2, py: 1.2 }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              color="#fff"
                            >
                              Allowance Type Summary
                            </Typography>
                          </Box>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: "#f0f7ff" }}>
                                <TableCell
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Type
                                </TableCell>
                                <TableCell
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Description
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Claims
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Total Amount
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(typeBreakdown)
                                .sort(([, a], [, b]) => b.total - a.total)
                                .map(([type, info], i) => (
                                  <TableRow
                                    key={type}
                                    sx={{
                                      bgcolor: i % 2 === 0 ? "#fafcff" : "#fff",
                                    }}
                                  >
                                    <TableCell>
                                      <Chip
                                        label={type}
                                        size="small"
                                        sx={{
                                          bgcolor: "#e3f2fd",
                                          color: "#1565c0",
                                          fontWeight: 700,
                                          fontSize: 11,
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>
                                      {info.desc}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{ fontSize: 12 }}
                                    >
                                      {info.count}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        fontWeight: 700,
                                        color: "#2e7d32",
                                        fontSize: 12,
                                      }}
                                    >
                                      {fmtCurrency(info.total)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              <TableRow sx={{ bgcolor: "#e8f5e9" }}>
                                <TableCell
                                  colSpan={2}
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Grand Total
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  {totalRecs}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#2e7d32",
                                    fontSize: 13,
                                  }}
                                >
                                  {fmtCurrency(grandTotal)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Paper>
                      </Grid>

                      {/* ── Employee Summary ── */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            border: "1px solid #e0e8f0",
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{ backgroundColor: "#1a3a5c", px: 2, py: 1.2 }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight={700}
                              color="#fff"
                            >
                              Employee-wise Summary
                            </Typography>
                          </Box>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: "#f0f7ff" }}>
                                <TableCell
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Employee
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Claims
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Days
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Amount
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredEmps.map((emp, i) => (
                                <TableRow
                                  key={emp.emp_id}
                                  sx={{
                                    bgcolor: i % 2 === 0 ? "#fafcff" : "#fff",
                                  }}
                                >
                                  <TableCell sx={{ fontSize: 12 }}>
                                    <Typography
                                      sx={{ fontSize: 12, fontWeight: 600 }}
                                    >
                                      {emp.emp_name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {emp.emp_id}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ fontSize: 12 }}
                                  >
                                    {emp.records.length}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ fontSize: 12 }}
                                  >
                                    {emp.total_days}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#2e7d32",
                                      fontSize: 12,
                                    }}
                                  >
                                    {fmtCurrency(emp.total_amount)}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow sx={{ bgcolor: "#e8f5e9" }}>
                                <TableCell
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  Grand Total
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  {totalRecs}
                                </TableCell>
                                <TableCell
                                  align="center"
                                  sx={{ fontWeight: 700, fontSize: 12 }}
                                >
                                  {filteredEmps.reduce(
                                    (s, e) => s + e.total_days,
                                    0,
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 700,
                                    color: "#2e7d32",
                                    fontSize: 13,
                                  }}
                                >
                                  {fmtCurrency(grandTotal)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Paper>
                      </Grid>
                    </Grid>
                  )}
                </>
              );
            })()}
          </>
        )}

        {/* Confirm Delete Dialog */}
        {getConfirmDeleteDialog({
          open: deleteDialogOpen,
          onClose: () => {
            setDeleteDialogOpen(false);
            setDeleteTargetId(null);
          },
          onConfirm: handleDeleteConfirm,
          title: "Delete Allowance",
          contentText:
            "Are you sure you want to delete this allowance record? This action cannot be undone.",
        })}

        {/* Add / Edit Dialog */}
        <ThemeProvider theme={FormThemes}>
          <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            fullWidth
            maxWidth="sm"
            sx={{
              "& .MuiDialog-paper": { borderRadius: 4, overflow: "visible" },
            }}
          >
            <DialogTitle
              sx={{
                bgcolor: "#f5f5f5",
                p: 1.5,
                fontSize: 15,
                fontWeight: 700,
                borderRadius: "12px 12px 0 0",
              }}
            >
              {isEditing ? "Edit Allowance Request" : "Add Allowance Request"}
              <IconButton
                onClick={() => setDialogOpen(false)}
                sx={{ position: "absolute", right: 8, top: 6 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{ p: "18px 16px", backgroundColor: "#fff" }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 2,
                }}
              >
                {/* Employee Name — read-only */}
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <TextField
                    label="Employee Name"
                    size="small"
                    fullWidth
                    disabled
                    value={empName || empId || ""}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      style: { fontWeight: 600, color: "#1a3a5c" },
                    }}
                  />
                </Box>

                {/* Allowance Type */}
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <TextField
                    select
                    label="Allowance Type *"
                    size="small"
                    fullWidth
                    value={form.allowance_id}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, allowance_id: e.target.value }))
                    }
                    error={!!formErrors.allowance_id}
                    helperText={formErrors.allowance_id}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="">
                      <em>Select allowance type</em>
                    </MenuItem>
                    {allowanceTypes.map((a) => (
                      <MenuItem key={a.allowance_id} value={a.allowance_id}>
                        {a.allowance_desc}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Amount — auto-filled */}
                {/* <TextField
                  label="Allowance Amount"
                  size="small"
                  disabled
                  value={
                    selectedTypeData
                      ? fmtCurrency(selectedTypeData.allowance_amount)
                      : "—"
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { color: "#2e7d32", fontWeight: 700 } }}
                /> */}

                {/* Allowance Date */}
                <TextField
                  label="Allowance Date *"
                  type="date"
                  size="small"
                  value={form.allowance_date}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, allowance_date: e.target.value }))
                  }
                  error={!!formErrors.allowance_date}
                  helperText={formErrors.allowance_date}
                  InputLabelProps={{ shrink: true }}
                />

                {/* Start Date */}
                <TextField
                  label="Start Date *"
                  type="date"
                  size="small"
                  value={form.start_date}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((p) => ({ ...p, start_date: val }));
                    setFormErrors((p) => ({
                      ...p,
                      start_date: "",
                      end_date:
                        form.end_date && val && val > form.end_date
                          ? "End date must be after start date"
                          : "",
                    }));
                  }}
                  error={!!formErrors.start_date}
                  helperText={formErrors.start_date}
                  InputLabelProps={{ shrink: true }}
                />

                {/* End Date */}
                <TextField
                  label="End Date *"
                  type="date"
                  size="small"
                  value={form.end_date}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((p) => ({ ...p, end_date: val }));
                    setFormErrors((p) => ({
                      ...p,
                      end_date:
                        form.start_date && val && val < form.start_date
                          ? "End date must be after start date"
                          : "",
                    }));
                  }}
                  error={!!formErrors.end_date}
                  helperText={formErrors.end_date}
                  InputLabelProps={{ shrink: true }}
                />

                {/* No of Days */}
                <TextField
                  label="No. of Days *"
                  type="number"
                  size="small"
                  value={form.no_of_days}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, no_of_days: e.target.value }))
                  }
                  error={!!formErrors.no_of_days}
                  helperText={
                    formErrors.no_of_days || "Auto-calculated from dates"
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 1 }}
                />

                {/* Notes */}
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <TextField
                    label="Notes (Optional)"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    value={form.notes}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, notes: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ maxLength: 500 }}
                    helperText={`${(form.notes || "").length}/500`}
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: "8px 20px", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setDialogOpen(false)}
                variant="outlined"
                color="inherit"
                sx={{ mr: 1, borderColor: "#ccc", color: "#555" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={saving}
                sx={{
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 3,
                  ...(isEditing
                    ? {
                        backgroundColor: "#4CAF50",
                        "&:hover": { backgroundColor: "#45a049" },
                      }
                    : {
                        backgroundColor: "#2DB0E4",
                        "&:hover": { backgroundColor: "#1D87B0" },
                      }),
                }}
              >
                {saving
                  ? "Saving..."
                  : isEditing
                    ? "Update"
                    : "Submit for Approval"}
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </ThemeProvider>
    </MainLayout>
  );
}
